// Pure helpers for the synthetic demo screens: no React, no browser APIs.
// Relative imports only, so tests/unit/shell-demo.test.ts can type-check and run
// this file without the '@/' path alias.
import { ROLES } from '../../domain/catalogue'
import type { DemoPersona } from '../../domain/types'

export type CatalogueEntry = {
  code: string
  name: string
  /** Plain-English role, shown before the server has answered. */
  role: string
  negative: boolean
}

/**
 * Copy of private.demo_persona_catalogue() (0016_rs_replaces_aa_om_role.sql), used by the join
 * form: a second device has no session yet, so it cannot ask the server for the list.
 * The server still checks every code and refuses an unknown one. A unit test keeps
 * this list in step with the migration. BadrEOR has no Area Authority from 0016: the RS
 * holds every AA duty and the Operations Manager (OM) approves each authorised permit.
 */
export const DEMO_PERSONA_CATALOGUE: readonly CatalogueEntry[] = [
  { code: 'PA', name: 'Synthetic Ahmed Al-Harthy', role: 'Permit Applicant', negative: false },
  { code: 'RS', name: 'Synthetic Khalid Al-Balushi', role: 'Responsible Supervisor', negative: false },
  { code: 'RS2', name: 'Synthetic Salim Al-Rashdi', role: 'Second Responsible Supervisor and gas tester', negative: false },
  { code: 'OM', name: 'Synthetic Sultan Al-Mahrouqi', role: 'Operations Manager', negative: false },
  { code: 'PH', name: 'Synthetic Fatma Al-Hinai', role: 'Permit Holder', negative: false },
  { code: 'AGT', name: 'Synthetic Omar Al-Maamari', role: 'Authorised Gas Tester', negative: false },
  { code: 'CREW1', name: 'Synthetic Ali Al-Abri', role: 'Work-party member', negative: false },
  { code: 'CREW2', name: 'Synthetic Maryam Al-Lawati', role: 'Work-party member', negative: false },
  { code: 'OAC', name: 'Synthetic Hassan Al-Yahyai', role: 'Other Affected Custodian', negative: false },
  { code: 'PC', name: 'Synthetic Nasser Al-Kindi', role: 'Production Coordinator', negative: false },
  { code: 'ADMIN', name: 'Synthetic Aisha Al-Saadi', role: 'Administrator, no signing authority', negative: false },
  { code: 'EXPIRED', name: 'Synthetic Hamad Al-Shukaili', role: 'Test case: expired PH licence', negative: true },
  { code: 'REVOKED', name: 'Synthetic Yousuf Al-Habsi', role: 'Test case: revoked PH licence', negative: true },
  { code: 'OUTSCOPE', name: 'Synthetic Saif Al-Rawahi', role: 'Test case: PH licence for another site', negative: true },
  { code: 'OUTSIDER', name: 'Synthetic Huda Al-Farsi', role: 'Test case: unrelated site', negative: true },
]

/**
 * Personas that exist only in sandboxes created before 0016 (served there by the legacy
 * catalogue function). Listed apart on the join form, so an older sandbox can still be
 * joined as its Area Authority.
 */
export const LEGACY_PERSONA_CATALOGUE: readonly CatalogueEntry[] = [
  { code: 'AA', name: 'Synthetic Salim Al-Rashdi', role: 'Area Authority and gas tester (older sandboxes)', negative: false },
]

const NEGATIVE_CODES = new Set(DEMO_PERSONA_CATALOGUE.filter((p) => p.negative).map((p) => p.code))

/** The catalogue row for a persona code: current personas first, then the older sandboxes' ones. */
export function catalogueEntry(code: string | null | undefined): CatalogueEntry | null {
  if (!code) return null
  return DEMO_PERSONA_CATALOGUE.find((p) => p.code === code) ?? LEGACY_PERSONA_CATALOGUE.find((p) => p.code === code) ?? null
}

/** Negative-case personas exist to show the server refusing; they are listed apart. */
export function isNegativeCase(p: { persona_code: string; description: string }): boolean {
  return NEGATIVE_CODES.has(p.persona_code) || /^\s*negative case/i.test(p.description)
}

export function splitPersonas<T extends { persona_code: string; description: string }>(list: readonly T[]): { lifecycle: T[]; negative: T[] } {
  const lifecycle: T[] = []
  const negative: T[] = []
  for (const p of list) (isNegativeCase(p) ? negative : lifecycle).push(p)
  return { lifecycle, negative }
}

export function humanise(code: string): string {
  const s = code.replace(/_/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : code
}

const ROLE_ORDER = Object.keys(ROLES)

function roleRank(code: string): number {
  const i = ROLE_ORDER.indexOf(code)
  return i === -1 ? ROLE_ORDER.length : i
}

/** Unique role codes in the catalogue order (PA, RS, OM, AA, PH, AGT, ...). */
export function uniqueRoleCodes(codes: readonly string[]): string[] {
  return [...new Set(codes)].sort((a, b) => roleRank(a) - roleRank(b) || a.localeCompare(b))
}

function roleInfo(code: string): { label: string; short: string } | null {
  return (ROLES as Record<string, { label: string; short: string }>)[code] ?? null
}

/** "Permit Applicant (PA)" for one role, "RS + AGT" for several, "" for none. */
export function rolesLabel(codes: readonly string[]): string {
  const list = uniqueRoleCodes(codes)
  if (list.length === 0) return ''
  if (list.length === 1) {
    const r = roleInfo(list[0])
    return r ? `${r.label} (${r.short})` : list[0]
  }
  return list.map((c) => roleInfo(c)?.short ?? c).join(' + ')
}

/** Short role summary for a persona: its credential roles, else its catalogue role. */
export function personaRoleSummary(p: Pick<DemoPersona, 'persona_code' | 'description' | 'credentials'>): string {
  const label = rolesLabel(p.credentials.map((c) => c.role_code))
  if (label) return label
  const entry = catalogueEntry(p.persona_code)
  if (entry) return entry.role
  const colon = p.description.indexOf(':')
  return colon > 0 ? p.description.slice(0, colon) : p.description
}

/** "Synthetic Ahmed Al-Harthy" -> "Ahmed Al-Harthy" (the synthetic strip is always shown). */
export function shortName(displayName: string): string {
  return displayName.replace(/^Synthetic\s+/i, '') || displayName
}

/** Sandbox codes are 8 symbols (older sandboxes) or 13 symbols (new ones). */
export const SANDBOX_CODE_LENGTHS: readonly number[] = [8, 13]

/** Accepts "abcd 2345", "ABCD-2345" or "abcd2345" and returns "ABCD2345": spaces and dashes are ignored. */
export function normaliseSandboxCode(raw: string): string {
  return raw.replace(/[\s\u2010-\u2015-]+/g, '').toUpperCase()
}

/** Reading groups: 8 symbols as 4 + 4, 13 as 4 + 4 + 5, anything else in fours. */
export function sandboxCodeGroups(code: string): string[] {
  if (code.length === 13) return [code.slice(0, 4), code.slice(4, 8), code.slice(8)]
  return code.match(/.{1,4}/g) ?? []
}

/** "ABCD2345" -> "ABCD 2345", "ABCDEFGHJKMNP" -> "ABCD EFGH JKMNP", for reading aloud or copying by hand. */
export function formatSandboxCode(code: string): string {
  return sandboxCodeGroups(code).join(' ')
}

/** What is wrong with a normalised code, in plain words, or null when it can be sent. Empty input is not a problem yet. */
export function sandboxCodeProblem(normalised: string): string | null {
  if (normalised === '') return null
  if (!/^[A-Z0-9]+$/.test(normalised)) return 'Use only letters and numbers. Spaces and dashes are ignored.'
  if (!SANDBOX_CODE_LENGTHS.includes(normalised.length)) {
    return `A sandbox code has 8 or 13 letters and numbers. You have entered ${normalised.length}.`
  }
  return null
}

export type PersonaUse = 'this_device' | 'elsewhere' | 'unused'

/**
 * Where a persona is signed in. `bound` comes from the server (an active sign-in exists
 * anywhere); `boundHere` is the list this browser keeps. A persona in use elsewhere moves
 * to this device when bound here, and the other device loses it.
 */
export function personaUse(p: { persona_code: string; bound: boolean }, boundHere: readonly string[]): PersonaUse {
  if (boundHere.includes(p.persona_code)) return 'this_device'
  return p.bound ? 'elsewhere' : 'unused'
}

export const PERSONA_USE_TEXT: Record<PersonaUse, string> = {
  this_device: 'Signed in on this device',
  elsewhere: 'In use in another browser or device',
  unused: 'Not used yet',
}

const CREDENTIAL_KIND_LABELS: Record<string, string> = {
  ptw_licence: 'PTW licence',
  agt_certificate: 'Gas tester certificate',
  appointment: 'Appointment',
}

export function credentialKindLabel(kind: string): string {
  return CREDENTIAL_KIND_LABELS[kind] ?? humanise(kind)
}

export type CredentialLine = {
  role: string
  roleLabel: string
  kind: string
  expiresOn: string | null
  revoked: boolean
  /** The stated expiry date is before today (display only; the server decides validity). */
  expiryPassed: boolean
}

export function credentialLines(p: Pick<DemoPersona, 'credentials'>, today: string): CredentialLine[] {
  return p.credentials.map((c) => ({
    role: c.role_code,
    roleLabel: roleInfo(c.role_code)?.label ?? c.role_code,
    kind: credentialKindLabel(c.credential_kind),
    expiresOn: c.expires_on,
    revoked: c.revoked,
    expiryPassed: Boolean(c.expires_on && c.expires_on.slice(0, 10) < today),
  }))
}

/** Today's date in Muscat (UTC+4, no daylight saving) as YYYY-MM-DD. */
export function muscatToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 4 * 3600_000).toISOString().slice(0, 10)
}

/** The SQLSTATE-style code on a server or auth error, if any. */
export function errorCode(e: unknown): string | null {
  if (e && typeof e === 'object' && 'code' in e) {
    const c = (e as { code: unknown }).code
    return typeof c === 'string' && c ? c : null
  }
  return null
}

export function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message
  if (e && typeof e === 'object' && 'message' in e) return String((e as { message: unknown }).message)
  return String(e)
}

/** Plain next step for the demo-session failures people actually hit. */
export function demoErrorHint(code: string | null, message = ''): string | null {
  switch (code) {
    case 'PW004':
      return 'This sign-in is not allowed to do that. If this persona was moved to another device, choose it again in the persona menu to use it here.'
    case 'PW005':
      return 'No demo or persona matches that code. Check the sandbox code and try again.'
    case 'PW006':
      return 'This browser sign-in already belongs to another persona. Leave the demo here and join again.'
    case 'PW008':
      return 'This environment does not run the synthetic demonstration.'
    default:
      if (!code && /fetch|network/i.test(message)) return 'Could not reach the server. Check the connection and try again.'
      if (/anonymous/i.test(message)) return 'The server does not allow demo sign-ins right now. Ask the system owner to enable them.'
      return null
  }
}
